import { Box } from '@mui/material';
import type { GridColDef } from '@mui/x-data-grid-pro';
import dayjs from 'dayjs';
import { useCallback, useMemo, useState } from 'react';
import { StyleObj } from '../../@types';
import { CasinoBetRow, CasinoBetsQueryParams } from '../../@types/casino';
import {
  appendCasinoTotalsRow,
  buildCasinoBetsTotalsRow,
  formatCasinoPercentage,
  getCasinoTotalsRowClass,
} from '../../helpers/casino';
import { createNumberColumn } from '../../helpers/columns';
import { useCasinoTableQuery } from '../../hooks/useCasinoTableQuery';
import { useCasinoBets } from '../../queries/casino';
import { createColumn } from '../../utils';
import DateRangeSelect, { DayjsTimestamp } from '../molecules/DateRangeSelect';
import TableTemplate from '../templates/TableTemplate';

const DEFAULT_SORT_COLUMN = 'createdAt';
const DEFAULT_SORT_ORDER = 'desc';

const createDefaultDateRange = (): DayjsTimestamp => ({
  fromTimestamp: dayjs().startOf('day'),
  toTimestamp: dayjs().endOf('day'),
});

const createDefaultQueryParams = (date: DayjsTimestamp): CasinoBetsQueryParams => ({
  page: 1,
  limit: 25,
  dateFrom: date.fromTimestamp.valueOf(),
  dateTo: date.toTimestamp.valueOf(),
  sortColumn: DEFAULT_SORT_COLUMN,
  sortOrder: DEFAULT_SORT_ORDER,
});

const EMPTY_BET_FIELDS: Array<Extract<keyof CasinoBetsQueryParams, string>> = [
  'username',
  'roundId',
  'providerName',
  'gameId',
  'payInAmount',
  'payOutAmount',
  'percentageGain',
  'betStatus',
];

const styles: StyleObj = {
  container: {
    position: 'relative',
    height: 'calc(100% - 200px)',
  },
};

const CasinoBetsPage = () => {
  const [date, setDate] = useState<DayjsTimestamp>(createDefaultDateRange);
  const { queryParams, paginationModel, updateQueryParams, changeQuery, handleSearch, handleSort, changeDateRange } =
    useCasinoTableQuery<CasinoBetsQueryParams>({
      defaultQuery: () => createDefaultQueryParams(date),
      emptyFields: EMPTY_BET_FIELDS,
      searchField: 'username',
      sortDefaults: {
        sortColumn: 'sortColumn',
        sortOrder: 'sortOrder',
        defaultColumn: DEFAULT_SORT_COLUMN,
        defaultOrder: DEFAULT_SORT_ORDER,
      },
    });
  const { data, isFetching, isLoading } = useCasinoBets(queryParams);

  const columns = useMemo<GridColDef<CasinoBetRow>[]>(
    () => [
      createColumn('createdAt', 'Created At', {
        minWidth: 180,
        valueGetter: (_value, row, _column, _apiRef) =>
          row.isTotalsRow ? 'Totals' : row.createdAt ? dayjs(row.createdAt).format('DD/MM/YYYY HH:mm:ss') : '',
      }),
      createColumn('username', 'Username', { minWidth: 160 }),
      createColumn('providerName', 'Provider Name', { minWidth: 180 }),
      createColumn('gameName', 'Game Name', { minWidth: 220, flex: 1 }),
      createColumn('roundId', 'Round ID', { minWidth: 180, sortable: false }),
      createNumberColumn('payInAmount', 'Pay In Amount', {
        minWidth: 150,
      }),
      createNumberColumn('payOutAmount', 'Pay Out Amount', {
        minWidth: 150,
      }),
      createColumn('percentageGain', 'Percentage Gain', {
        minWidth: 150,
        valueFormatter: (value, row, column, apiRef) => formatCasinoPercentage(value),
      }),
      createColumn('entityName', 'Entity Name', { minWidth: 180 }),
    ],
    []
  );

  const handleDateChange = useCallback(
    (newDate: DayjsTimestamp) => {
      setDate(newDate);
      changeDateRange({
        dateFrom: newDate.fromTimestamp.valueOf(),
        dateTo: newDate.toTimestamp.valueOf(),
      });
    },
    [changeDateRange]
  );

  const rows = appendCasinoTotalsRow(data?.items ?? [], buildCasinoBetsTotalsRow(data?.totals));

  return (
    <Box sx={styles.container}>
      <TableTemplate
        rows={rows}
        columns={columns}
        loading={isFetching || isLoading}
        rowCount={data?.count || 0}
        handlePaginationModelChange={updateQueryParams}
        handleSearch={handleSearch}
        handleSort={handleSort}
        changeQuery={changeQuery}
        getRowClassName={getCasinoTotalsRowClass}
        paginationModel={paginationModel}
        filterType='casino-bets'
        customTableHeaderContent={<DateRangeSelect date={date} onDateChange={handleDateChange} />}
        showFilter
      />
    </Box>
  );
};

export default CasinoBetsPage;
